import React, { useState, useLayoutEffect, useRef, useMemo, useEffect } from "react";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import OutsideClickHandler from "react-outside-click-handler";

import { getAvatarUrl } from "src/Common/common";

const UserDropDown = ({ options, selectedUser, onSelect, isOpen }) => {
  const triggerRef = useRef(null);
  const inputRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState({ visibility: "hidden" });
  const [searchTerm, setSearchTerm] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const filteredOptions = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const preparedOptions = options.map((option, originalIndex) => {
      const name = option?.name ?? "";
      const lowerName = name.toLowerCase();
      const firstName = lowerName.split(/\s+/)[0] ?? "";
      const firstNameMatch = normalizedSearch ? firstName.includes(normalizedSearch) : false;
      const nameMatch = normalizedSearch ? lowerName.includes(normalizedSearch) : true;

      return {
        option,
        originalIndex,
        nameMatch,
        matchRank: firstNameMatch ? 0 : 1,
      };
    });

    if (!normalizedSearch) {
      return preparedOptions.map(({ option, originalIndex }) => ({ option, originalIndex }));
    }

    return preparedOptions
      .filter(({ nameMatch }) => nameMatch)
      .sort((a, b) => a.matchRank - b.matchRank || a.originalIndex - b.originalIndex)
      .map(({ option, originalIndex }) => ({ option, originalIndex }));
  }, [options, searchTerm]);

  const handleUserClick = (index) => {
    onSelect(index);
  };

  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuStyle((prev) => ({ ...prev, visibility: "hidden" }));
      return;
    }
    if (!triggerRef.current) return;

    const updateMenuPosition = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuStyle({
        position: "fixed",
        top: rect.bottom + 0,
        left: rect.left,
        width: rect.width,
        zIndex: 9999,
        visibility: "visible",
      });
    };

    updateMenuPosition();
    const raf = requestAnimationFrame(updateMenuPosition);
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
      setActiveIndex(0);
      return;
    }

    const selectedIndex = filteredOptions.findIndex(({ option }) => option?.id === selectedUser?.id);
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);

    const raf = requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [isOpen, filteredOptions, selectedUser]);

  useEffect(() => {
    if (!searchTerm) return;
    setActiveIndex(0);
  }, [searchTerm]);

  const handleKeyDown = (event) => {
    if (!isOpen) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((prev) => Math.min(prev + 1, Math.max(filteredOptions.length - 1, 0)));
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      const chosen = filteredOptions[activeIndex] ?? filteredOptions[0];
      if (chosen) {
        onSelect(chosen.originalIndex);
      }
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      onSelect(-1);
    }
  };

  return (
    <OutsideClickHandler
      onOutsideClick={() => {
        onSelect(-1);
      }}
    >
      <div className="relative w-full">
        <div
          ref={triggerRef}
          data-testid="assign-user-select"
          onClick={() => {
            onSelect(isOpen ? -1 : -2);
          }}
          className="w-full flex items-center h-[30px] justify-between px-4 py-2 border rounded-[6px] text-gray-800 focus:outline-none focus-visible:outline-none focus:ring-0 cursor-pointer"
        >
          {/* <span className="font-OpenSansRegular text-[400] text-[12px] text-titleColor">{defaultOption}</span> */}
          <div>
            {selectedUser && (
              <div className=" py-2 cursor-pointer flex gap-3 items-center">
                <img src={getAvatarUrl(selectedUser.avatar)} className="w-[15px] h-[15px] rounded-[7.5px]" />
                <span className="font-OpenSansRegular text-[12px]">{selectedUser.name}</span>
              </div>
            )}
          </div>
          {isOpen ? <FiChevronUp className="ml-2" size={15} /> : <FiChevronDown className="ml-2" size={15} />}
        </div>
        {isOpen && (
          <div style={menuStyle} className="bg-white border rounded-md shadow-lg">
            <div className="px-3 py-1 border-b">
              <input
                ref={inputRef}
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                onKeyDown={handleKeyDown}
                data-testid="assign-user-search"
                placeholder="Search..."
                className="w-full text-[12px] outline-none"
              />
            </div>
            <ul className="max-h-[160px] overflow-y-auto">
              {filteredOptions.length === 0 && (
                <li className="px-4 py-2 text-[12px] text-gray-500">No users found</li>
              )}
              {filteredOptions.map(({ option, originalIndex }, index) => (
                <li
                  key={option.id ?? originalIndex}
                  data-testid="assign-user-option"
                  onClick={() => handleUserClick(originalIndex)}
                  onMouseEnter={() => setActiveIndex(index)}
                  className={`px-4 py-2 cursor-pointer flex gap-3 items-center ${index === activeIndex ? "bg-gray-200" : "hover:bg-gray-200"
                    }`}
                >
                  <img src={getAvatarUrl(option.avatar)} className="w-[15px] h-[15px] rounded-[7.5px]" />
                  <span className="font-OpenSansRegular text-[12px]">{option.name}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </OutsideClickHandler>
  );
};

export default UserDropDown;
